import { faPen, faPlus } from '@fortawesome/pro-solid-svg-icons';
import classnames from 'classnames';
import { useCallback, useState } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { FilterSearchInput } from '@sorare/core/src/components/search/FilterSearchInput';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import type { ExtendedUIState } from '@sorare/core/src/components/search/InstantSearch/types';
import { Option } from '@sorare/core/src/components/search/Option';
import { useSearchCardsContext } from '@sorare/core/src/contexts/searchCards';
import { useVirtualToggle } from '@sorare/core/src/hooks/useVirtualToggle';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import { VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import { filters, glossary } from '@sorare/core/src/lib/glossary';

export const FILTER = VIRTUAL_TOGGLE_FILTERS.customDecksFilter;

const messages = defineMessages({
  createNewList: {
    id: 'CustomDecksFiler.createNewList',
    defaultMessage: 'Create new list',
  },
  showMore: {
    id: 'CustomDecksFilter.showMore',
    defaultMessage: 'Show more',
  },
});

const DecksContainer = styled.div`
  position: relative;
  max-height: calc(20 * var(--unit));
  overflow: hidden;
  &.expanded {
    max-height: none;
  }
`;
const ShowMore = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  color: var(--c-white);
  background-color: var(--c-black);
`;
const Search = styled.div`
  display: grid;
  gap: var(--unit);
  grid-template-columns: 1fr min-content;
  width: 100%;
`;
const AfterOptionWrapper = styled.span`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const AfterOption = ({ onEdit }: { onEdit: () => void }) => {
  const { formatMessage } = useIntl();
  return (
    <AfterOptionWrapper>
      <IconButton
        icon={faPen}
        onClick={onEdit}
        size="small"
        aria-label={formatMessage(glossary.edit)}
        color="transparent"
      />
    </AfterOptionWrapper>
  );
};

type Props<T extends { slug: string; name: string; tokensCount: number }> = {
  decks: T[];
  loading: boolean;
  search: string;
  setSearch: (search: string) => void;
  onEditDeck: (slug: string) => void;
  onCreateDeck: () => void;
  visible: boolean;
  showCurrentUserDecks?: boolean;
};

export const CustomDecksFilter = <
  T extends { slug: string; name: string; tokensCount: number },
>({
  decks,
  loading,
  search,
  setSearch,
  onEditDeck,
  onCreateDeck,
  visible,
}: Props<T>) => {
  const { formatMessage } = useIntl();
  const { editableLists, favoriteFilter: favoriteFilterCurrentRefinement } =
    useSearchCardsContext();
  const [expanded, setExpanded] = useState(false);
  const setVirtualToggle = useVirtualToggleManager();

  const { currentRefinement } = useVirtualToggle<string>({
    name: FILTER.name,
  });
  const onChange = useCallback(
    (deck: string) => {
      const isActive = currentRefinement === deck;
      const newValue = isActive ? undefined : deck;
      setVirtualToggle({
        [FILTER.name]: newValue,
      });
    },
    [currentRefinement, setVirtualToggle]
  );

  const displayShowMore = !expanded && decks.length > 5;

  if (!visible) return <FilterSection />;

  return (
    <FilterSection
      search={
        <Search>
          <FilterSearchInput
            handleChange={e => {
              setSearch(e.target.value);
            }}
            value={search}
          />

          {!!editableLists && (
            <IconButton
              icon={faPlus}
              onClick={() => onCreateDeck()}
              color="tertiary"
              size="small"
              aria-label={formatMessage(messages.createNewList)}
            />
          )}
        </Search>
      }
    >
      <DecksContainer className={classnames({ expanded: !displayShowMore })}>
        {!!loading && !decks.length && <LoadingIndicator small />}
        <Option
          variant="checkbox"
          active={favoriteFilterCurrentRefinement}
          onClick={() => {
            setVirtualToggle({
              [VIRTUAL_TOGGLE_FILTERS.favoriteFilter.name]:
                !favoriteFilterCurrentRefinement,
            });
          }}
        >
          {formatMessage(filters.favoriteFilterLabel)}
        </Option>
        {decks.map(deck => {
          const { name, slug, tokensCount } = deck;
          return (
            <Option
              key={slug}
              active={currentRefinement === name}
              onClick={() => onChange(name)}
              variant="checkbox"
              count={tokensCount}
              afterLabel={
                !!editableLists && (
                  <AfterOption onEdit={() => onEditDeck(slug)} />
                )
              }
            >
              {name}
            </Option>
          );
        })}
        {!!displayShowMore && (
          <ShowMore>
            <Button
              size="small"
              fullWidth
              color="transparent"
              onClick={() => setExpanded(true)}
            >
              <FormattedMessage {...messages.showMore} />
            </Button>
          </ShowMore>
        )}
      </DecksContainer>
    </FilterSection>
  );
};

export const Title = () => {
  const { indexUiState } = useInstantSearch<ExtendedUIState>();
  const selected = !!indexUiState?.virtualToggle?.[FILTER.name];

  return <FilterTitle name={FILTER.title!} selected={selected} />;
};
